import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosError,
} from "axios";

import {
  readSessionToken,
  redirectToPortalLogin,
  refreshSessionToken,
  resolveRequestToken,
  SessionExpiredError,
} from "./auth";

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
class Service<_T> {
  protected api: AxiosInstance;

  protected constructor(public baseURL: string) {
    this.api = axios.create({
      baseURL,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=utf-8",
      },
    });

    this.api.interceptors.request.use((config) =>
      this.requestInterceptor(config),
    );

    this.api.interceptors.response.use(
      (response) => response,
      (error) => this.responseInterceptor(error),
    );
  }

  private endSession(message: string): never {
    redirectToPortalLogin();
    throw new SessionExpiredError(message);
  }

  private async requestInterceptor(
    config: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> {
    let token: string | undefined;

    try {
      token = await resolveRequestToken();
    } catch {
      this.endSession("Session expirée, reconnexion au portail requise");
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  }

  private async responseInterceptor(error: AxiosError): Promise<never> {
    const originalRequest = error.config as RetryableRequestConfig | undefined;

    if (
      !originalRequest ||
      originalRequest._retry ||
      error.response?.status !== 401
    ) {
      return Promise.reject(error);
    }

    const staleToken = readSessionToken();
    if (!staleToken) {
      this.endSession("Authentification requise");
    }

    originalRequest._retry = true;

    let newToken: string;
    try {
      newToken = await refreshSessionToken(staleToken);
    } catch {
      this.endSession("Session expirée, reconnexion au portail requise");
    }

    originalRequest.headers.Authorization = `Bearer ${newToken}`;

    return this.api(originalRequest);
  }
}

export default Service;
